import React from "react";
import { TextField, Button, Grid, IconButton, Autocomplete, Switch, FormControlLabel, Typography } from "@mui/material";
import ModalLayout from "components/ModalLayout";
import MDButton from "components/MDButton";
import CancelButton from "components/CancelButton";
import DeleteIcon from "@mui/icons-material/Delete";

function ServiceFormModal({
  open,
  handleClose,
  isEditing,
  serviceData,
  options,
  types,
  serviceTypes = [],
  handleInputChange,
  handleFileChange,
  handleSubmit,
  handleAddOption,
  handleOptionChange,
  handleRemoveOption,
}) {
  return (
    <ModalLayout
      open={open}
      onClose={handleClose}
      title={isEditing ? "Edit Service" : "Add Service"}
      subtitle="Service"
      maxWidth={960}
      headerContent={
        <Typography variant="body2" color="text.secondary" mt={1}>
          Configure multilingual copy, assign service types, toggle requirements, and define options.
        </Typography>
      }
      contentSx={{
        overflowY: "auto",
        maxHeight: "calc(90vh - 170px)", // Adjust based on header/footer height
      }}
      actions={
        <>
          <CancelButton onClick={handleClose} />
          <MDButton
            type="submit"
            form="service-form"
            variant="gradient"
            color="info"
            sx={{ minWidth: 140 }}
          >
            {isEditing ? "Update" : "Add"}
          </MDButton>
        </>
      }
    >
      <form id="service-form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title (EN)"
              name="name_en"
              value={serviceData.name_en}
              onChange={handleInputChange}
              margin="normal"
              required
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title (FR)"
              name="name_fr"
              value={serviceData.name_fr}
              onChange={handleInputChange}
              margin="normal"
              required
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Description (EN)"
              name="description_en"
              value={serviceData.description_en}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Description (FR)"
              name="description_fr"
              value={serviceData.description_fr}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={types}
              getOptionLabel={(option) => option.label}
              value={types.find((type) => type.value === serviceData.type) || null}
              onChange={(event, newValue) => {
                handleInputChange({ target: { name: "type", value: newValue ? newValue.value : null } });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Type" InputProps={{ ...params.InputProps, sx: { borderRadius: 2 } }} />
              )}
              sx={{ my: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={serviceTypes}
              getOptionLabel={(option) => `${option.name_en || ""}${option.name_fr ? " / " + option.name_fr : ""}`}
              value={serviceTypes.find((st) => st.id === serviceData.service_type_id) || null}
              onChange={(event, newValue) => {
                handleInputChange({ target: { name: "service_type_id", value: newValue ? newValue.id : "" } });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Service Type" InputProps={{ ...params.InputProps, sx: { borderRadius: 2 } }} />
              )}
              sx={{ my: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(serviceData.is_required)}
                  onChange={(event) =>
                    handleInputChange({ target: { name: "is_required", value: event.target.checked } })
                  }
                  color="primary"
                />
              }
              label="Is Required"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input type="file" id="file-input" onChange={(e) => handleFileChange(e, "image")} style={{ display: "none" }} />
              <label htmlFor="file-input">
                <Button variant="outlined" component="span" sx={{ width: "100%", color: "gray", my: 2 }}>
                  Upload Image
                </Button>
              </label>
              {serviceData.image_preview && (
                <img
                  src={serviceData.image_preview}
                  alt="Preview"
                  style={{ width: "100px", height: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}
                />
              )}
            </div>
          </Grid>

          <Grid item xs={12}>
            <Button variant="outlined" onClick={handleAddOption} sx={{ width: { xs: "100%", sm: "25%" }, color: "gray", mb: 2 }}>
              Add Option
            </Button>

            <Grid container spacing={2}>
              {options.map((option, index) => (
                <Grid item xs={12} key={index}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label={`Option ${index + 1} - Name (EN)`}
                        value={option.name_en || ""}
                        required
                        onChange={(e) => handleOptionChange(index, "name_en", e.target.value)}
                        margin="normal"
                        InputProps={{ sx: { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label={`Option ${index + 1} - Name (FR)`}
                        value={option.name_fr || ""}
                        required
                        onChange={(e) => handleOptionChange(index, "name_fr", e.target.value)}
                        margin="normal"
                        InputProps={{ sx: { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label={`Option ${index + 1} - Price`}
                        type="text"
                        inputMode="decimal"
                        value={option.price || ""}
                        onKeyDown={(event) => {
                          if (["e", "E", "+", "-"].includes(event.key)) {
                            event.preventDefault();
                          }
                        }}
                        onChange={(event) => {
                          const { value } = event.target;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            handleOptionChange(index, "price", value);
                          }
                        }}
                        margin="normal"
                        InputProps={{ sx: { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton onClick={() => handleRemoveOption(index)} color="error" size="small" sx={{ mt: 1 }}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </form>
    </ModalLayout>
  );
}

export default ServiceFormModal;
